import React, { useEffect, useRef, useState } from "react";
import { useAsyncEffect } from "tap/utils/hooks/useAsyncEffect";
import { useLoading } from "tap/utils/hooks/useLoading";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Row } from "antd";
import Button from "@dtci/spellbook/Button";
import Input from "@dtci/spellbook/Input";
import Select from "@dtci/spellbook/Select";
import { MultiSelect } from "@dtci/spellbook/MultiSelect";
import { InputLabel } from "tap/components/InputLabel";
import { useJobsData } from "../../hooks/useJobsData";
import { useJobsActions } from "tap/store/tapJobs/useJobs.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { tapApi } from "tap/api.js";
import { parseSearchParams, updateSearchParams } from "tap/utils/urlParams.js";
import { getItemRange } from "tap/utils/pagination.js";
import { validateSpecialCharacters } from "tap/utils/validateSpecialCharacters.js";
import { cleanJobFilter, buildFilterFromParams, getDefaultTapJobsFilter, hasTapJobsFilterParams, shouldDeferInitialTapJobsFetch } from "./utils";
import "./styles.scss";

/**
 * Search form for filtering the TAP Jobs grid.
 *
 * Available filters:
 * - Job IDs: comma-separated job ID search
 * - Job Types: single-select dropdown for job type
 * - Job Statuses: multi-select for job status
 *
 * Default: Job Status is set to "Running" with no job type when no URL params present.
 * Filter state syncs bidirectionally with URL params for shareable searches.
 * Changes trigger debounced searches (400ms delay).
 */
export function JobsSearchForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { fetchJobs, updateJobs, tapJobRange, tapJobSort, itemsPerPage } =
    useJobsData();
  const { updateJobsSearchForm, clearJobs } = useJobsActions();
  const { showErrorToast } = useToasts();
  const { filterForm, jobStatusOptions, jobTypeOptions } = useSelector(
    (s) => s.tapJobs,
  );

  const [validationErrors, setValidationErrors] = useState({
    jobId: undefined,
  });
  const debounceTimerRef = useRef(null);
  const fetchRequestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useAsyncEffect(async ({ isMounted }) => {
    try {
      const [jobTypeRes, jobStatusRes] = await Promise.all([
        tapApi.jobTypes(),
        tapApi.jobStatuses(),
      ]);

      if (!isMounted()) return;

      const jobTypes = Array.isArray(jobTypeRes)
        ? jobTypeRes.map(({ name, id }) => ({ label: name, value: id }))
        : [];
      const jobStatuses = Array.isArray(jobStatusRes)
        ? jobStatusRes.map(({ name, id }) => ({ label: name, value: id }))
        : [];
      updateJobs({ jobTypeOptions: jobTypes, jobStatusOptions: jobStatuses });

      // Apply default filters when no URL filter params (initial page load)
      const params = parseSearchParams(location.search);
      if (!hasTapJobsFilterParams(params)) {
        const defaultFilter = getDefaultTapJobsFilter(jobStatuses);
        if (defaultFilter) {
          updateJobsSearchForm({
            key: "filterForm",
            data: defaultFilter,
          });
          const pageSize = params.pageSize
            ? Number(params.pageSize)
            : itemsPerPage;
          const range = getItemRange(0, pageSize);
          const data = await fetchJobs(range, defaultFilter);
          if (isMounted() && data) {
            updateJobs({
              jobs: data?.content,
              totalPages: data?.totalPages,
              totalItems: data?.totalElements,
              currentPage: data?.pageable?.pageNumber,
              tapJobRange: range,
            });
          }
        }
      }
    } catch (error) {
      console.error("Failed to load initial options:", error);
      if (isMounted()) {
        showErrorToast("Failed to load filter options");
        updateJobs({ jobTypeOptions: [], jobStatusOptions: [] });
      }
    }
  }, []);

  // Sync URL params to form state on navigation (location.key changes on each navigation)
  useEffect(() => {
    const params = parseSearchParams(location.search);
    const filterData = buildFilterFromParams(
      params,
      jobTypeOptions,
      jobStatusOptions,
    );

    let rangeOverride;
    if (filterData.pageSize) {
      rangeOverride = getItemRange(0, filterData.pageSize);
      updateJobs({ itemsPerPage: filterData.pageSize });
    }

    const hasUrlParams = Object.keys(filterData).length > 0;

    if (hasUrlParams) {
      updateJobsSearchForm({ key: "filterForm", data: filterData });
    }

    if (shouldDeferInitialTapJobsFetch(params, isInitialMount.current)) {
      isInitialMount.current = false;
      return;
    }

    // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
    // only carries partial params (e.g. pageSize).
    const filterForFetch =
      hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

    fetchData(filterForFetch, rangeOverride);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Re-parse URL params when options load (select values depend on options being available)
  useEffect(() => {
    if (isInitialMount.current) return;

    const params = parseSearchParams(location.search);
    const hasSelectParams = params.jobType || params.jobStatus;

    if (
      hasSelectParams &&
      (jobTypeOptions.length > 0 || jobStatusOptions.length > 0)
    ) {
      const filterData = buildFilterFromParams(
        params,
        jobTypeOptions,
        jobStatusOptions,
      );
      const mergedFilterData = { ...filterForm, ...filterData };
      updateJobsSearchForm({ key: "filterForm", data: filterData });
      fetchData(mergedFilterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobTypeOptions, jobStatusOptions]);

  const fetchData = async (filterData, rangeOverride) => {
    const requestId = ++fetchRequestIdRef.current;
    setLoadingTrue();
    const range = rangeOverride ?? getItemRange(0, itemsPerPage);
    const data = await fetchJobs(range, filterData);

    if (requestId !== fetchRequestIdRef.current) {
      setLoadingFalse();
      return;
    }

    if (data) {
      updateJobs({
        jobs: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber ?? 0,
        tapJobRange: range,
      });
    }
    setLoadingFalse();
  };

  // Reset navigates to clean URL and re-applies default filters
  const handleResetFilter = async () => {
    navigate(location.pathname);
    clearJobs();
    setValidationErrors({ jobId: undefined });

    const defaultFilter = getDefaultTapJobsFilter(jobStatusOptions);
    if (defaultFilter) {
      updateJobsSearchForm({
        key: "filterForm",
        data: defaultFilter,
      });
      await fetchData(defaultFilter);
    } else {
      await fetchData({});
    }
  };

  const handleInputChange = (field, value) => {
    let newValidationErrors = validationErrors;
    if (["jobId"].includes(field)) {
      const error = validateSpecialCharacters(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    }

    const newFilterForm = { ...filterForm, [field]: value };
    updateJobsSearchForm({
      key: "filterForm",
      data: { [field]: value },
    });

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const hasErrors = Object.values(newValidationErrors).some(
        (error) => error,
      );
      if (!hasErrors) {
        const filtered = cleanJobFilter(
          newFilterForm,
          getItemRange(0, itemsPerPage),
          tapJobSort,
        );
        updateSearchParams(location, navigate, filtered);
      }
    }, 400);
  };

  return (
    <Row className="jobs-filter-form">
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Job IDs">
          <Input
            value={filterForm?.jobId || ""}
            onChange={(e) => handleInputChange("jobId", e.target.value)}
            width="auto"
            id="jobId"
            name="jobId"
            placeholder="Enter"
            className={validationErrors.jobId ? "input-error" : ""}
          />
          {validationErrors.jobId && (
            <div className="validation-error">{validationErrors.jobId}</div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Job Types">
          <Select
            options={jobTypeOptions}
            isClearable
            onChange={(value) => handleInputChange("jobType", value)}
            value={filterForm?.jobType || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Job Statuses">
          <MultiSelect
            onChange={(value) => handleInputChange("jobStatus", value)}
            name="jobStatus"
            options={jobStatusOptions}
            isSearchable
            isClearable
            value={filterForm.jobStatus || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <Button
          className="reset-button"
          sizeType="small"
          styleType="standard"
          onClick={handleResetFilter}
        >
          Reset
        </Button>
      </Col>
    </Row>
  );
}
